import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import Loader from 'apollo-react/components/Loader';
import Tooltip from 'apollo-react/components/Tooltip';
import Info from 'apollo-react-icons/Info';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import ActionsButton from './components/ActionsButton';
import HeaderInfo from './components/HeaderInfo';
import SaveAsModal from './components/SaveAsModal';
import SaveConfirmationModal from './components/SaveConfirmationModal';
import UserChangesButtons from './components/UserChangesButtons';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { REPORT_MODEL_DESIGNER, SMART_INSIGHT_REPORT_MODELS } from '../../../constants/Urls';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  deleteReportModel,
  exportReportModel,
  saveReportModel,
} from '../../actions/report-model-designer.actions';
import { HEADER_ITEM_DEFAULT_LABEL } from '../../constants/report-model-designer.constants';
import {
  getReportModelInfo,
  getReportModelName,
  isDeleteDisabled,
  isExportDisabled,
  isSaveDisabled,
} from '../../selectors/report-model-designer.selectors';
import { equalityFn, modifyDate } from '../../utils/report-model-designer-utils';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px',
    '& ul': {
      marginLeft: '15px',
    },
    marginLeft: 15,
  },
  headerInfo: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
    alignItems: 'center',
    height: 44,
  },
  infoToolTip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  titleWrapper: {
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
});

const Header = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const translateString = useTranslation();

  const modelInfo = useSelector(getReportModelInfo);
  const isSaveButtonDisabled = useSelector(isSaveDisabled, equalityFn);
  const isDeleteButtonDisabled = useSelector(isDeleteDisabled, equalityFn);
  const isExportButtonDisabled = useSelector(isExportDisabled, equalityFn);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaveAsModalOpen, setSaveAsModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const reportName = useSelector(getReportModelName);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const deleteConfirmMessage = translateString(
    'A deleted model cannot be recovered and will cause all reports using this model to fail. Are you sure you want to delete this report model?'
  );

  const redirectToReportModel = ({ reportName, reportId }) => {
    history.push(
      `${REPORT_MODEL_DESIGNER}?reportKey=${reportName}&reportId=${reportId}&standaloneLayout=true`
    );
  };

  const redirectToReportModelDashboard = () => {
    history.push(SMART_INSIGHT_REPORT_MODELS);
  };

  const confirmDeleteReportModel = async () => {
    setIsLoading(true);

    setDeleteConfirmationModalOpen(false);

    await dispatch(deleteReportModel({ redirectToReportModelDashboard }));

    setIsLoading(false);
  };

  const saveReport = async () => {
    if (isConfirmModalOpen) {
      setIsConfirmModalOpen(false);
    }

    setIsLoading(true);

    await dispatch(saveReportModel({ isSaveAs: true, reportName, redirectToReportModel }));

    setIsLoading(false);
  };

  const handleDelete = () => {
    setDeleteConfirmationModalOpen(true);
  };

  const handleCancel = () => {
    redirectToReportModelDashboard();
  };

  const handleExport = async () => {
    await dispatch(exportReportModel());
  };

  const actionItems = [
    {
      text: translateString('Save As'),
      onClick: () => setSaveAsModalOpen(true),
      hidden: false,
    },
    {
      text: translateString('Export'),
      onClick: handleExport,
      hidden: isExportButtonDisabled,
    },
    {
      text: translateString('Delete'),
      onClick: handleDelete,
      hidden: isDeleteButtonDisabled,
    },
  ];

  const compactModeActionItems = [
    {
      text: translateString('Save'),
      onClick: saveReport,
      disabled: isSaveButtonDisabled,
    },
    {
      text: translateString('Save As'),
      onClick: () => setSaveAsModalOpen(true),
    },
    {
      text: translateString('Cancel'),
      onClick: handleCancel,
    },
    { isDivider: true },
    {
      text: translateString('Export'),
      onClick: handleExport,
      hidden: isExportButtonDisabled,
    },
    { isDivider: true },
    {
      text: translateString('Delete'),
      onClick: handleDelete,
      hidden: isDeleteButtonDisabled,
    },
  ];

  const infoArr = [
    { title: `Name: ${modelInfo.ReportModelName}`, subtitle: '' },
    {
      title: `Last Modified Date: ${modifyDate(modelInfo.ReportLastModified) ??
        HEADER_ITEM_DEFAULT_LABEL}`,
      subtitle: '',
    },
  ];

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.titleWrapper} item xs={8}>
          <span className={classes.infoToolTip}>
            <Tooltip extraLabels={infoArr} placement="right" variant="light">
              <IconButton aria-label="Info Icon">
                <Info color="primary" />
              </IconButton>
            </Tooltip>
          </span>
          <span className={classes.headerInfo}>
            <HeaderInfo modelInfo={modelInfo} />
          </span>
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <UserChangesButtons />

          <ActionsButton
            actionItems={actionItems}
            compactModeActionItems={compactModeActionItems}
          />

          <Button
            variant="text"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleCancel}
          >
            {translateString('Cancel')}
          </Button>

          <Button
            variant="secondary"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={saveReport}
            disabled={isSaveButtonDisabled}
          >
            {translateString('Save')}
          </Button>

          {isLoading && <Loader isInner />}
        </Grid>
      </Grid>
      {isSaveAsModalOpen && (
        <SaveAsModal
          isOpen={isSaveAsModalOpen}
          initialReportName={reportName}
          handleClose={() => setSaveAsModalOpen(false)}
          setIsLoading={setIsLoading}
          redirectToReportModel={redirectToReportModel}
        />
      )}

      {isDeleteConfirmationModalOpen && (
        <DeleteConfirmationModal
          message={deleteConfirmMessage}
          open={isDeleteConfirmationModalOpen}
          onClose={() => setDeleteConfirmationModalOpen(false)}
          onDeleteConfirm={confirmDeleteReportModel}
        />
      )}

      {isConfirmModalOpen && (
        <SaveConfirmationModal
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={saveReport}
          isOpen={isConfirmModalOpen}
          reportName={reportName}
        />
      )}
    </div>
  );
};

export default Header;
